import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BadgeCheck,
  Loader2,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Settings2,
  Trash2,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/hooks/use-toast';
import {
  ShopifyPlaybookKey,
  ShopifyPlaybookStatusEntry,
  shopifyPlaybooksApi,
} from '@/lib/api/services/shopifyPlaybooks';
import { PlaybookDialog } from './PlaybookDialog';
import { resolvePlaybookIcon } from './playbookIcons';

interface ShopifyPlaybooksProps {
  businessAccountId: string;
  isConnected: boolean;
  variant?: 'cards' | 'rows';
}

export const ShopifyPlaybooks: React.FC<ShopifyPlaybooksProps> = ({
  businessAccountId,
  isConnected,
  variant = 'cards',
}) => {
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState<ShopifyPlaybookStatusEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutatingKey, setMutatingKey] = useState<string | null>(null);
  const [dialogPlaybook, setDialogPlaybook] = useState<ShopifyPlaybookStatusEntry | null>(null);
  const [inlineKey, setInlineKey] = useState<string | null>(null);

  const openInlinePicker = useCallback((key: string) => {
    setInlineKey(key);
    requestAnimationFrame(() => {
      document
        .querySelector('[data-testid="shopify-playbook-inline"]')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, []);

  const [unavailable, setUnavailable] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!businessAccountId) return;
    setLoading(true);
    const res = await shopifyPlaybooksApi.list(businessAccountId);
    if ((res as any)?.code === 'WORKSPACE_NO_WHATSAPP') {
      setUnavailable((res as any).error);
      setPlaybooks([]);
    } else if ((res as any)?.error) {
      toast({
        title: 'Could not load auto messages',
        description: (res as any).error,
        variant: 'destructive',
      });
      setUnavailable(null);
      setPlaybooks([]);
    } else {
      setUnavailable(null);
      setPlaybooks(((res as any)?.data as ShopifyPlaybookStatusEntry[]) || []);
    }
    setLoading(false);
  }, [businessAccountId]);

  useEffect(() => {
    if (!businessAccountId) return;
    refresh();
  }, [businessAccountId, refresh]);

  async function handleToggle(playbook: ShopifyPlaybookStatusEntry) {
    if (!playbook.flowId) return;
    const nextStatus = playbook.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setMutatingKey(playbook.key);
    const res = await shopifyPlaybooksApi.update(playbook.key as ShopifyPlaybookKey, {
      status: nextStatus,
      businessAccountId,
    });
    setMutatingKey(null);
    if ((res as any)?.error || (res as any)?.success === false) {
      toast({
        title: 'Could not update auto message',
        description: (res as any).error || 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    toast({ variant: "success",
      title: nextStatus === 'ACTIVE' ? 'Auto message resumed' : 'Auto message paused',
      description: playbook.title,
    });
    refresh();
  }

  async function handleDelete(playbook: ShopifyPlaybookStatusEntry) {
    if (!playbook.flowId) return;
    const confirmed = window.confirm(
      `Remove the "${playbook.title}" auto message? The underlying automation flow will be deleted. This does not affect your WhatsApp template.`
    );
    if (!confirmed) return;
    setMutatingKey(playbook.key);
    const res = await shopifyPlaybooksApi.remove(playbook.key as ShopifyPlaybookKey, businessAccountId);
    setMutatingKey(null);
    if ((res as any)?.error || (res as any)?.success === false) {
      toast({
        title: 'Could not delete auto message',
        description: (res as any).error || 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    toast({ variant: "success", title: 'Auto message removed', description: playbook.title });
    refresh();
  }

  function handleCustomize(playbook: ShopifyPlaybookStatusEntry) {
    if (!playbook.flowId) return;
    navigate(`/home/automation/flows/${playbook.flowId}`);
  }

  if (variant === 'rows') {
    if (!isConnected) {
      return (
        <p className="py-6 text-center text-[13px] text-[#6A7282]">
          Connect a Shopify store to enable auto messages.
        </p>
      );
    }

    if (loading && playbooks.length === 0) {
      return (
        <div className="flex items-center justify-center py-8 text-[13px] text-[#6A7282]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading auto messages…
        </div>
      );
    }

    if (unavailable) {
      return (
        <p className="py-6 text-center text-[13px] text-[#6A7282]" data-testid="shopify-playbooks-unavailable">
          {unavailable}
        </p>
      );
    }

    return (
      <div className="space-y-4" data-testid="shopify-playbooks">
        {playbooks.map((playbook) => {
          const isActive = playbook.status === 'ACTIVE';
          const isConfigured = !!playbook.flowId;
          const isMutating = mutatingKey === playbook.key;
          const isExpanded = inlineKey === playbook.key;

          return (
            <div key={playbook.key} className="space-y-3">
            <div
              data-testid={`shopify-playbook-card-${playbook.key}`}
              className="flex items-start justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[14px] font-semibold text-[#101828]">
                    {playbook.title}
                  </span>
                  {isActive ? (
                    <BadgeCheck
                      className="h-4 w-4 shrink-0 text-[#12B76A]"
                      aria-label="Active"
                    />
                  ) : null}
                </div>
                <p className="mt-0.5 text-[13px] leading-snug text-[#6A7282]">
                  {playbook.description}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {isMutating ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[#98A2B3]" />
                ) : null}

                <Switch
                  checked={isActive}
                  disabled={isMutating}
                  aria-label={playbook.title}
                  data-testid={`shopify-playbook-toggle-${playbook.key}`}
                  onCheckedChange={() => {
                    if (isConfigured) handleToggle(playbook);
                    else setInlineKey(isExpanded ? null : playbook.key);
                  }}
                />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-[#667085]"
                      disabled={isMutating}
                      aria-label={`More actions for ${playbook.title}`}
                      data-testid={`shopify-playbook-more-${playbook.key}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {isConfigured ? (
                      <>
                        <DropdownMenuItem
                          onSelect={() => openInlinePicker(playbook.key)}
                          data-testid={`shopify-playbook-edit-${playbook.key}`}
                        >
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit template
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleCustomize(playbook)}
                          data-testid={`shopify-playbook-customize-${playbook.key}`}
                        >
                          <Settings2 className="mr-2 h-3.5 w-3.5" />
                          Customize
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-[#B42318] focus:text-[#B42318]"
                          onSelect={() => handleDelete(playbook)}
                          data-testid={`shopify-playbook-delete-${playbook.key}`}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        onSelect={() => openInlinePicker(playbook.key)}
                        data-testid={`shopify-playbook-setup-${playbook.key}`}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Set up
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {isExpanded ? (
              <PlaybookDialog
                open
                variant="inline"
                playbook={playbook}
                businessAccountId={businessAccountId}
                onOpenChange={(open) => {
                  if (!open) setInlineKey(null);
                }}
                onSaved={() => {
                  setInlineKey(null);
                  refresh();
                }}
              />
            ) : null}
          </div>
          );
        })}

      </div>
    );
  }

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Connect a Shopify store on the Connect tab to enable auto messages.
        </CardContent>
      </Card>
    );
  }

  if (unavailable) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="shopify-playbooks-unavailable">
          {unavailable}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="shopify-playbooks">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Auto Messages</h2>
          <p className="text-sm text-muted-foreground">
            Pick an approved WhatsApp template for each event. We wire the automation flow for you —
            no drag-drop needed. Click <span className="font-medium">Customize</span> on any enabled
            auto message to open it in the full flow builder.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="shopify-playbooks-refresh"
        >
          {loading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Refresh
        </Button>
      </div>

      {loading && playbooks.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading auto messages…
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {playbooks.map((playbook) => {
            const Icon = resolvePlaybookIcon(playbook.icon);
            const isActive = playbook.status === 'ACTIVE';
            const isPaused = playbook.status === 'PAUSED';
            const isConfigured = !!playbook.flowId;
            const isMutating = mutatingKey === playbook.key;

            return (
              <Card
                key={playbook.key}
                data-testid={`shopify-playbook-card-${playbook.key}`}
                className={
                  isActive
                    ? 'border-[#95BF47]/40 shadow-sm'
                    : isPaused
                      ? 'border-amber-300/50'
                      : 'border-border'
                }
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isActive ? 'bg-[#95BF47] text-white' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="truncate">{playbook.title}</span>
                        {isActive && (
                          <Badge className="bg-[#95BF47]/10 text-[#5a7d2a] hover:bg-[#95BF47]/15">
                            Active
                          </Badge>
                        )}
                        {isPaused && (
                          <Badge variant="outline" className="border-amber-400 text-amber-700">
                            Paused
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {playbook.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Event: <code className="font-mono text-[11px]">{playbook.triggerType}</code>
                  </div>

                  {isConfigured ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDialogPlaybook(playbook)}
                        disabled={isMutating}
                        data-testid={`shopify-playbook-edit-${playbook.key}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit template
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggle(playbook)}
                        disabled={isMutating}
                        data-testid={`shopify-playbook-toggle-${playbook.key}`}
                      >
                        {isMutating && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        {!isMutating &&
                          (isActive ? (
                            <PowerOff className="h-3.5 w-3.5 mr-1.5" />
                          ) : (
                            <Power className="h-3.5 w-3.5 mr-1.5" />
                          ))}
                        {isActive ? 'Pause' : 'Resume'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCustomize(playbook)}
                        disabled={isMutating}
                        data-testid={`shopify-playbook-customize-${playbook.key}`}
                      >
                        <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                        Customize
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(playbook)}
                        disabled={isMutating}
                        data-testid={`shopify-playbook-delete-${playbook.key}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setDialogPlaybook(playbook)}
                      className="bg-[#95BF47] hover:bg-[#7a9d3a] text-white"
                      data-testid={`shopify-playbook-enable-${playbook.key}`}
                    >
                      Enable
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PlaybookDialog
        open={!!dialogPlaybook}
        onOpenChange={(open) => {
          if (!open) setDialogPlaybook(null);
        }}
        playbook={dialogPlaybook}
        businessAccountId={businessAccountId}
        onSaved={refresh}
      />
    </div>
  );
};

export default ShopifyPlaybooks;
